import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

gsap.registerPlugin(ScrollTrigger);

const { FiArrowUpRight, FiUsers, FiTrendingUp, FiAward } = FiIcons;
const STATEMENT = 'Leadership is not just a title, but the practice of building what lasts.';

const AboutSection = () => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. Setup: split into words while preserving readable spacing.
      // Each word is one animation unit, so lines never break inside words.
      const textElement = textRef.current;
      textElement.replaceChildren();
      
      const words = STATEMENT.split(' ').map((word, index, allWords) => {
        const span = document.createElement('span');
        span.textContent = word;
        span.style.display = 'inline-block';
        span.style.marginRight = index === allWords.length - 1 ? '0' : '0.22em';
        span.className = 'scatter-word';
        textElement.appendChild(span);
        return span;
      });

      // 2. Pre-Pin Entrance Animation
      // Fade in the text block before pinning starts for a smoother entry
      gsap.from(textRef.current, {
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 85%",
          end: "top 40%",
          scrub: 1
        },
        y: 80,
        opacity: 0,
        filter: "blur(10px)",
        ease: "power2.out"
      });

      // 3. Create the Main Pinning Timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=200%",
          scrub: 1,
          pin: true,
          anticipatePin: 1
        }
      });

      // 4. Animation Sequence
      tl.to(words, {
        x: () => (Math.random() - 0.5) * window.innerWidth * 1.5,
        y: () => (Math.random() - 0.5) * window.innerHeight * 1.5,
        rotation: () => Math.random() * 720 - 360,
        opacity: 0,
        scale: () => Math.random() * 2 + 0.5,
        duration: 2,
        ease: "power3.inOut",
        stagger: {
          amount: 0.5,
          from: "center"
        }
      })
      .to(profileRef.current, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 1,
        ease: "power2.out"
      }, "-=0.5");

    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div id="about" ref={containerRef} className="relative min-h-screen bg-black text-white overflow-hidden flex flex-col items-center justify-center z-10">
      
      {/* 1. The Exploding Text Layer */}
      <div className="absolute inset-0 flex items-center justify-center p-4 md:p-12 z-20 pointer-events-none">
        <h2 ref={textRef} className="text-4xl md:text-7xl lg:text-8xl font-black text-center leading-tight uppercase tracking-tighter max-w-6xl mix-blend-difference">
          {STATEMENT}
        </h2>
      </div>

      {/* 2. The Profile Content Layer (Hidden initially) */}
      <div ref={profileRef} className="relative z-10 flex flex-col items-center text-center opacity-0 transform translate-y-12 scale-95 max-w-5xl px-6">
        
        {/* Tech Icons Row */}
        <div id="stack" className="flex gap-8 mb-12 text-[#e2c96e]">
          <SafeIcon icon={FiUsers} className="text-3xl" />
          <SafeIcon icon={FiTrendingUp} className="text-3xl" />
          <SafeIcon icon={FiAward} className="text-3xl" />
        </div>

        {/* Intro Text */}
        <div className="space-y-6">
          <h3 className="text-xl md:text-2xl font-medium tracking-widest uppercase text-gray-300">
            Hello! <br /> I'm Katherine William-Botrous
          </h3>
          
          <div className="pt-8">
            <div className="flex items-center justify-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-widest mb-6">
              My Mission <SafeIcon icon={FiArrowUpRight} />
            </div>
            <p className="text-lg md:text-2xl font-bold uppercase leading-relaxed max-w-3xl text-gray-200">
              As a Vice President of Sales and experienced operations leader, I build high-performance teams, strengthen customer relationships, and turn complex business challenges into measurable outcomes.
            </p>
            
            <div className="mt-8 flex flex-col gap-4 text-xs font-mono text-gray-400">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="border border-[#4a4a4a] p-2 rounded hover:border-[#e2c96e] transition-colors text-center">Sales Leadership</div>
                <div className="border border-[#4a4a4a] p-2 rounded hover:border-[#e2c96e] transition-colors text-center">Customer Success</div>
                <div className="border border-[#4a4a4a] p-2 rounded hover:border-[#e2c96e] transition-colors text-center">Customer Service</div>
                <div className="border border-[#4a4a4a] p-2 rounded hover:border-[#e2c96e] transition-colors text-center">Operations</div>
              </div>
              <div className="grid grid-cols-2 gap-4 md:max-w-[50%] mx-auto">
                <div className="border border-[#4a4a4a] p-2 rounded hover:border-[#e2c96e] transition-colors text-center">Team Development</div>
                <div className="border border-[#4a4a4a] p-2 rounded hover:border-[#e2c96e] transition-colors text-center">Operational Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Decor */}
      <div className="absolute top-8 left-8 text-6xl font-black text-gray-800 opacity-20 select-none z-0">
        101
      </div>
      <div className="absolute top-8 right-8 text-xl font-bold text-gray-600 z-0">
        SALES / OPERATIONS
      </div>
    </div>
  );
};

export default AboutSection;